import * as Sentry from "@sentry/react";
import React from "react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { createRoot } from "react-dom/client"; // Import createRoot
import "./index.css";
import { NextUIProvider } from "@nextui-org/react";
import App from "./App";
import MainProvider from "./context/MainProvider";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import { SocketProvider } from './context/SocketProvider'
import { getClubId } from "./api/CalendarBooking";
import { isPublicFormsPath } from "./utils/publicFormsPath";
import FlowAccountTaxDocumentsHost from "./features/FlowAccount/FlowAccountTaxDocumentsHost";
import "./i18n";
import {
  isChunkLoadError,
  reloadOnceOnChunkError,
} from "./utils/reloadOnChunkError";
import { isInjectedPageScriptError, isRecoveredDndInvariant, isRecoveredStaleChunkParse } from "./utils/sentryNoise";
import { withOpenDialogs } from "./utils/openDialogsMarker";
import { clubScopedQueryKeyHash } from "./utils/clubTabSync";

// Sentry has to init before any error can fire. Kept right after imports so
// it runs before any module-level side effects below, and well before React
// mounts at the bottom of this file. DSN points at fe-bookgo (admin web).
// Mirror every Sentry error into Ground Zero (our in-house error dashboard) in
// addition to sentry.io. GZ speaks the Sentry envelope protocol, so we POST the
// already-built event as a one-item envelope. The project slug rides in the DSN
// public-key slot (GZ reads ?sentry_key); hyphens aren't allowed there, so
// fe-admin is sent as fe_admin and GZ maps it back. Production-only and
// fire-and-forget — a GZ outage never affects the app or sentry.io.
const GZ_ENVELOPE_URL =
  "https://groundzzero-production.up.railway.app/api/1/envelope/?sentry_key=fe_admin";

function forwardToGroundZero(event: { event_id?: string }): void {
  if (process.env.NODE_ENV !== "production") return;
  try {
    const body =
      JSON.stringify({ event_id: event.event_id, sent_at: new Date().toISOString() }) +
      "\n" +
      JSON.stringify({ type: "event" }) +
      "\n" +
      JSON.stringify(event);
    void fetch(GZ_ENVELOPE_URL, {
      method: "POST",
      headers: { "content-type": "application/x-sentry-envelope" },
      body,
      keepalive: true, // survive page unload — errors often precede navigation
    }).catch(() => {
      /* best-effort mirror — ignore network errors */
    });
  } catch {
    /* never let GZ forwarding break the app */
  }
}

Sentry.init({
  dsn: "https://c8fe1a477f4bdc29f648d1939e032f7e@o4510859590631424.ingest.us.sentry.io/4510859629559808",
  environment: process.env.NODE_ENV,
  // Build commit, set by the deploy workflow. Tells errors from tabs opened
  // before a deploy apart from errors in the new build (GZ#3000).
  release: process.env.REACT_APP_RELEASE || undefined,
  sendDefaultPii: true,
  beforeSend(event) {
    // Drag-and-drop library error it already recovers from (GZ#2268).
    if (isRecoveredDndInvariant(event)) return null;
    // Old chunk after a deploy; the tab reloads itself (GZ#4380).
    if (isRecoveredStaleChunkParse(event)) return null;
    // Code the browser or an add-on put into the page, not ours (GZ#2998).
    if (isInjectedPageScriptError(event)) return null;
    // Name the open popups so a crash we cannot reproduce shows its screen (GZ#2998).
    withOpenDialogs(event);
    // Send a copy to Ground Zero, then let it continue on to sentry.io.
    forwardToGroundZero(event);
    return event;
  },
});

// Prevent infinite refresh loops - check if we already refreshed recently.
// GZ#2227: this must be evaluated at CALL time. It used to be computed once at
// module load, so a page that booted <30s after a chunk-refresh could never
// refresh again for its whole lifetime — users got stuck on the
// ChunkErrorBoundary spinner with no way out.
const hasRecentChunkRefresh = () => {
  const lastRefresh = localStorage.getItem('last-chunk-refresh');
  return !!lastRefresh && Date.now() - parseInt(lastRefresh) < 30000; // 30 seconds
};

// Force clear all caches and service workers, then reload.
// `force` skips the loop guard — used by the manual "Refresh Now" button.
// GZ#XXXX (Jungle Padel): add cache-busting to the reload URL so stale CDN
// copies are bypassed, similar to the chunk retry mechanism.
const forceRefresh = async (force?: boolean) => {
  const now = Date.now();
  // Prevent infinite loops - if we refreshed in last 30 seconds, don't refresh
  // again automatically. Surface the update modal instead so the user always
  // has a working manual refresh button (never a dead spinner).
  if (!force && hasRecentChunkRefresh()) {
    console.log('Skipping auto-refresh - already refreshed recently');
    showUpdateModal();
    return;
  }

  // Mark that we're refreshing
  localStorage.setItem('last-chunk-refresh', now.toString());

  try {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }

    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // Clear localStorage cache markers (but not the refresh timestamp)
    localStorage.removeItem('app-version');
    localStorage.removeItem('sw-cleaned');

    // Force hard reload with cache-buster to bypass stale CDN copies
    const url = new URL(window.location.href);
    url.searchParams.delete('cache-bust');
    url.searchParams.delete('refresh');
    url.searchParams.set('v', now.toString());
    window.location.replace(url.toString());
  } catch (e) {
    // If anything fails, force reload with cache-buster
    const url = new URL(window.location.href);
    url.searchParams.set('v', Date.now().toString());
    window.location.replace(url.toString());
  }
};

// Make it available globally for the refresh button
(window as any).forceRefresh = forceRefresh;

// Show a branded update modal instead of browser confirm
const showUpdateModal = () => {
  // Prevent showing multiple modals
  if (document.getElementById('chunk-error-modal')) return;

  const modal = document.createElement('div');
  modal.id = 'chunk-error-modal';
  modal.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    ">
      <div style="
        background: white;
        border-radius: 16px;
        padding: 32px;
        max-width: 380px;
        width: 90%;
        text-align: center;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      ">
        <div style="
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #E8F4FD;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        ">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22356D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
        </div>
        <h2 style="
          font-size: 20px;
          font-weight: 600;
          color: #22356D;
          margin: 0 0 12px;
        ">Update Available</h2>
        <p style="
          font-size: 14px;
          color: #22356D99;
          margin: 0 0 24px;
          line-height: 1.5;
        ">We've made some improvements! Please refresh to get the latest version.</p>
        <button onclick="window.forceRefresh(true)" style="
          background: #22356D;
          color: white;
          border: none;
          border-radius: 10px;
          padding: 14px 32px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(34, 53, 109, 0.3);
          transition: background 0.2s;
        " onmouseover="this.style.background='#1a2b55'" onmouseout="this.style.background='#22356D'">
          Refresh Now
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
};

// GZ#2754 / GZ#2712: same one-reload guard as lazyWithChunkReload. Catches
// script-tag / JSONP chunk misses that never go through React.lazy.
// GZ#2045: do not treat every /static/js/ runtime error as a chunk miss —
// isChunkLoadError matches the message (and, for Unexpected token '<', the
// hashed chunk filename), not "any error whose filename is the bundle".
window.addEventListener('error', (event) => {
  const error = event.error instanceof Error
    ? event.error
    : new Error(event.message || "");
  if (!isChunkLoadError(error, { filename: event.filename || "" })) return;
  event.preventDefault();
  if (!reloadOnceOnChunkError(error, undefined, { filename: event.filename || "" })) {
    showUpdateModal();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (!isChunkLoadError(event.reason)) return;
  event.preventDefault();
  if (!reloadOnceOnChunkError(event.reason)) {
    showUpdateModal();
  }
});

// Create a client. The active club is part of every query hash, so one
// club's cached data can never be served for another.
const queryClient = new QueryClient({
  defaultOptions: { queries: { queryKeyHashFn: clubScopedQueryKeyHash } },
});

// Select the root element
const rootElement = document.getElementById("root");

// Check if rootElement is not null
if (!rootElement) throw new Error("Failed to find the root element");

// Create a root
const root = createRoot(rootElement);

// Render the app using the root
root.render(
  <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={theme}>
      <NextUIProvider>
        <MainProvider>
          {/* Public Forms pages (/forms/<club>/...) have no admin session: no calendar socket. */}
          <SocketProvider
            initialUrl={`${process.env.REACT_APP_BACKEND_URL}/calendars/${getClubId()}/updates`}
            enabled={!isPublicFormsPath(window.location.pathname)}
          >
            <App />
            <FlowAccountTaxDocumentsHost />
          </SocketProvider>
        </MainProvider>
      </NextUIProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

// ONE-TIME cleanup for old service workers (from previous deployments)
// This runs once per user and never again
if ('serviceWorker' in navigator && !localStorage.getItem('sw-cleaned')) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    if (registrations.length) {
      console.log('Cleaning up old service workers...');
      Promise.all(registrations.map(reg => reg.unregister()))
        .then(() => {
          localStorage.setItem('sw-cleaned', 'true');
          window.location.reload();
        });
    } else {
      localStorage.setItem('sw-cleaned', 'true');
    }
  });
}
